import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { MapPin, Phone, Mail, Clock, Send, ExternalLink } from 'lucide-react';
import { practiceInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie alle Pflichtfelder aus.",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/contact`, {
        name: contactForm.name.trim(),
        email: contactForm.email.trim(),
        phone: contactForm.phone.trim() || null,
        subject: contactForm.subject.trim() || null,
        message: contactForm.message.trim()
      });

      toast({
        title: "Nachricht gesendet!",
        description: "Vielen Dank für Ihre Nachricht. Wir melden uns schnellstmöglich bei Ihnen.",
      });

      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form submission error:', error);
      toast({
        title: "Fehler beim Senden",
        description: "Entschuldigung, es gab ein Problem beim Senden Ihrer Nachricht. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    }
  };

  return (
    <section id="kontakt" className="py-20 bg-gradient-to-br from-red-50 via-white to-red-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Kontakt & Anfahrt
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Wir sind gerne für Sie da. Kontaktieren Sie uns für Termine, 
            Fragen oder Feedback.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-red-100">
              <CardHeader className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-t-lg">
                <CardTitle className="text-xl">Kontaktinformationen</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="flex items-start">
                  <MapPin className="h-6 w-6 text-red-600 mr-4 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Adresse</h4>
                    <p className="text-gray-600">
                      {practiceInfo.address.street}<br />
                      {practiceInfo.address.city}<br />
                      {practiceInfo.address.country}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="h-6 w-6 text-red-600 mr-4 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Telefon</h4>
                    <p className="text-gray-600">{practiceInfo.contact.phone}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Für Terminvereinbarungen und Anfragen
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="h-6 w-6 text-red-600 mr-4 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">E-Mail</h4>
                    <p className="text-gray-600">
                      <a 
                        href="mailto:terminanfrage@frauenarzt-praxis-gronau.de"
                        className="text-red-600 hover:text-red-700 underline"
                      >
                        {practiceInfo.contact.email}
                      </a>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Für Terminanfragen per E-Mail
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-red-600 mr-4 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Online Terminbuchung</h4>
                    <p className="text-gray-600 mb-3">24/7 verfügbar</p>
                    <Button 
                      className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                      onClick={() => window.open(practiceInfo.contact.booking_url, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Jetzt Termin buchen
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Interactive Google Maps */}
          <div className="space-y-8">
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-red-100">
              <CardHeader className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-t-lg">
                <CardTitle className="text-xl">Anfahrt</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="relative">
                  <div className="bg-gradient-to-br from-red-100 to-red-200 rounded-lg overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2410.1234567890123!2d9.740000000000000!3d52.083333333333330!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b046a0a0a0a0a0%3A0x0!2sBlanke%20Str.%2017%2C%2031028%20Gronau%20(Leine)%2C%20Deutschland!5e0!3m2!1sde!2sde!4v1640995200000!5m2!1sde!2sde"
                      width="100%"
                      height="400"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Frauenarztpraxis Dr. med. Christina Weiß - Standort"
                      className="rounded-lg"
                    ></iframe>
                  </div>
                  <div className="mt-4 text-center">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent('Blanke Str. 17, 31028 Gronau (Leine), Deutschland')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      In Google Maps öffnen
                    </a>
                  </div>
                </div>
                <div className="mt-4 space-y-2 text-sm text-gray-600">
                  <p><strong>Adresse:</strong> {practiceInfo.address.street}, {practiceInfo.address.city}</p>
                  <p><strong>Parkmöglichkeiten:</strong> Kostenfreie Parkplätze direkt vor der Praxis verfügbar</p>
                  <p><strong>Öffentliche Verkehrsmittel:</strong> Bushaltestelle "Gronau Zentrum" in 2 Minuten Fußweg</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;